import React from "react";
import { useNavigate } from "react-router-dom";
import { route } from "../../../../routes";
import { CustomerApplicationStepNumber } from "./customerApplicationSteps";

interface CircleProps {
  style?: React.CSSProperties;
  label: string;
  step: CustomerApplicationStepNumber;
}

const StepCircleFullNonActive: React.FC<CircleProps> = ({ style, label }) => {
  return (
    <div title={label} className="cursor-pointer">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
        <circle cx="5" cy="5" r="5" fill="#D9D9D9" />
      </svg>
    </div>
  );
};

const StepCircleFullActive: React.FC<CircleProps> = ({ style, label, step }) => {
  const navigate = useNavigate();

  return (
    <div title={label} className="cursor-pointer" onClick={() => navigate(route.customerApplication(step))}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
        <circle cx="5" cy="5" r="5" fill="#215383" />
      </svg>
    </div>
  );
};

const StepCircleEmpty: React.FC<CircleProps> = ({ style, label }) => {
  return (
    <div title={label} className="cursor-pointer">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
        <circle cx="7" cy="7" r="7" fill="#215383" />
        <circle cx="7" cy="7" r="4" fill="white" />
      </svg>
    </div>
  );
};

interface LineProps {
  style?: React.CSSProperties;
}

const windowInnerWidth = window.innerWidth > 1080 ? 1080 : window.innerWidth - 80;
const STEP_DISTANCE = windowInnerWidth / 7;

const StepFillLine: React.FC<CircleProps> = ({ style }) => {
  return (
    <svg
      width={STEP_DISTANCE}
      height="3"
      viewBox={`0 0 ${STEP_DISTANCE} 3`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <rect width={STEP_DISTANCE} height="3" rx="1.5" fill="#215383" />
    </svg>
  );
};

const StepBackGroundLine: React.FC<LineProps> = ({ style }) => {
  return (
    <svg
      width={windowInnerWidth}
      height="3"
      viewBox={`0 0 ${windowInnerWidth} 3`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <rect width={windowInnerWidth} height="3" rx="1.5" fill="#EDEDED" />
    </svg>
  );
};
export interface StepIndicatorProps {
  currentStep: CustomerApplicationStepNumber;
  steps: { [key: number]: { label: string } };
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, steps }) => {
  const stepArray = Object.keys(steps).map((a) => Number(a) as CustomerApplicationStepNumber);
  const stepCount = stepArray.length;
  const currentStepIndex = stepArray.indexOf(currentStep);

  if (currentStep < 1) throw Error(`Steps are numbered from 1, but ${currentStep} given.`);

  const elements = [];
  for (let i = 0; i < currentStepIndex; i++) {
    const step = stepArray[i];

    elements.push(
      <StepCircleFullActive
        step={step}
        key={`${step}-point`}
        style={{ position: "absolute", top: "-4", left: i * STEP_DISTANCE }}
        label={steps[step].label}
      />,
    );
    elements.push(
      <StepFillLine
        step={step}
        key={`${step}-line`}
        style={{ position: "absolute", top: "-1", left: i * STEP_DISTANCE + 4 }}
        label={steps[step].label}
      />,
    );
  }
  elements.push(
    <StepCircleEmpty
      step={currentStep}
      key={`${currentStep}-point`}
      style={{ position: "absolute", top: "-6", left: currentStepIndex * STEP_DISTANCE }}
      label={steps[currentStep].label}
    />,
  );

  for (let i = currentStepIndex + 1; i < stepCount; i++) {
    const step = stepArray[i];
    elements.push(
      <StepCircleFullNonActive
        step={step}
        key={`${step}-point`}
        style={{ position: "absolute", top: "-4", left: i * STEP_DISTANCE }}
        label={steps[step].label}
      />,
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <StepBackGroundLine />
      {elements}
    </div>
  );
};
